/**
 * CompleteView — presentational component for the COMPLETE status branch of GameScreen.
 *
 * Renders the spectator layout: event log + cog on the LEFT, map on the RIGHT
 * in landscape/wide. Reuses the same layout as the "not your turn" spectator branch.
 * The EndGameModal renders on top (mounted in GameScreen's overlay block).
 *
 * ALL hooks remain in GameScreen. This component is purely presentational —
 * it receives every piece of state and every callback as a prop.
 */

import React from 'react'
import { View } from 'react-native'
import { usePortraitMapReserves } from '../../../hooks/usePortraitMapReserves'
import { MapView } from '../../map/MapView'
import { MapControlsBarSlot } from '../../map/MapControlsBar'
import { RecentActivitySidebar, recentActivitySidebarWidth } from '../RecentActivitySidebar'
import { HistoricalActiveView } from '../HistoricalActiveView'
import type { HydratedGameState } from '../../../lib/gameState'
import type { GameResponse } from '@warcarp/api-types'
import type { PanOffset } from '../../../lib/mapZoom'
import { shouldShowCompletedActivity } from '../../../lib/gameScreenState'
import type { ActivityFilterState } from '../../../lib/activityFilters'

export interface CompleteViewProps {
  game: GameResponse
  gameState: HydratedGameState
  myPlayer: { slot: number } | null | undefined
  isLandscape: boolean
  isDesktop: boolean
  inHistoricalView: boolean
  historicalGameState: HydratedGameState | null
  historicalEventIndex: number | null
  onViewAtEvent: (index: number) => void
  onScrubEvent: (index: number) => void
  onReturnToLive: () => void
  /** Desktop/web: arrow keys step the snapshot log (see HistoricalActiveView). */
  snapshotArrowKeysEnabled?: boolean
  /**
   * Same chat bar as the live game (input + log controls, including grow and
   * shrink). Replaces the old "See all →" header: a finished game is where
   * players talk, and the bar's grow toggle expands the log.
   */
  chatBar?: React.ReactNode
  /** Log filters chosen in the chat bar's controls. */
  logFilters?: ActivityFilterState
  /** The chat-only list, when the chat bar's CHAT view is on (same as the live game). */
  renderChatOnlyLog?: (args: { orientation: 'bottom-panel' | 'left-sidebar'; topContent?: React.ReactNode }) => React.ReactNode

  // Corner overlays
  regionToggleNode: React.ReactNode
  regionToggleCorner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  zoomToggleNode: React.ReactNode
  zoomToggleCorner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  /** Thin control bar rendered directly beneath the map (zoom + view mode). */
  mapControlsBar?: React.ReactNode

  // Region details state (for the region bonus overlay when in complete view)
  regionDetailsMode: boolean
  focusedRegionId: string | null
  regionColorById: Record<string, string>

  // Zoom / pan
  zoomEnabled: boolean
  zoomScale: number
  panOffset: PanOffset
  onPanChange: (next: PanOffset) => void
  onZoomScaleChange: (scale: number) => void

  // Action panel header (landscape cog + roster row)
  landscapeActionHeader: React.ReactNode

  // WS connected-at ref value (passed to MapView viewerJoinedAt, epoch ms)
  viewerJoinedAt: number
}

export function CompleteView({
  game,
  gameState,
  myPlayer,
  isLandscape,
  isDesktop,
  inHistoricalView,
  historicalGameState,
  historicalEventIndex,
  onViewAtEvent,
  onScrubEvent,
  onReturnToLive,
  snapshotArrowKeysEnabled = false,
  chatBar,
  logFilters,
  renderChatOnlyLog,
  regionToggleNode,
  regionToggleCorner,
  zoomToggleNode,
  zoomToggleCorner,
  mapControlsBar,
  regionDetailsMode,
  focusedRegionId,
  regionColorById,
  zoomEnabled,
  zoomScale,
  panOffset,
  onPanChange,
  onZoomScaleChange,
  landscapeActionHeader,
  viewerJoinedAt,
}: CompleteViewProps) {
  // The recent-activity sidebar renders the wire EVENT TAIL. To map a tapped row
  // to its ABSOLUTE index in the full log (what the time-machine replays), the
  // sidebar adds this offset = total events − tail length. Without it, clicks
  // resolve to early (tail-relative) indices and the snapshot shows the wrong
  // (game-start-ish) board. Same computation ActiveGameView does for live play.
  const liveEventOffset = Math.max(
    0,
    (gameState.eventCount ?? 0) - (gameState.events?.length ?? 0),
  )
  const portraitReserves = usePortraitMapReserves(gameState.map)
  const hasActivity = shouldShowCompletedActivity(gameState)
  return (
    // The time-machine snapshot sits OVER the complete layout (sibling, visible
    // only when inHistoricalView), mirroring ActiveGameView — so a finished game
    // can still review the log: tapping a recent-activity event reconstructs the
    // board. The complete layout stays mounted underneath (display:none).
    <View className="flex-1">
    <View className="flex-1" style={inHistoricalView ? { display: 'none' } : undefined}>
    {/* ── COMPLETE: spectator layout — log + cog on the LEFT, map on
        the RIGHT in landscape/wide. The EndGameModal renders on top
        (see overlay block in GameScreen) until the user dismisses it. */}
    <View className={isLandscape ? 'flex-1 flex-row' : 'flex-1'}>
      {isLandscape && hasActivity && renderChatOnlyLog && renderChatOnlyLog({ orientation: 'left-sidebar', topContent: <>{landscapeActionHeader}{chatBar}</> })}
      {isLandscape && hasActivity && !renderChatOnlyLog && (
        <RecentActivitySidebar
          gameId={game.id}
          state={gameState}
          players={game.players}
          topContent={<>{landscapeActionHeader}{chatBar}</>}
          onViewAtEvent={onViewAtEvent}
          hideHeader
          selectedEventIndex={historicalEventIndex}
          eventIndexOffset={liveEventOffset}
          infiniteScroll
          filters={logFilters}
        />
      )}
      {/* The landscape navigation header normally lives inside the activity
          sidebar. Keep its fixed-width column when the event log was purged
          so completed games still expose Back and Chat beside the map. */}
      {isLandscape && !hasActivity && (
        <View
          style={{ width: recentActivitySidebarWidth(isDesktop) }}
          className="border-r border-olive bg-canvas"
        >
          {landscapeActionHeader}
        </View>
      )}
      {isLandscape ? (
        <View className="flex-1">
          <View className={`${isDesktop ? 'items-start' : `flex-1 items-start ${inHistoricalView ? 'justify-start' : 'justify-center'}`}`}>
            <MapView
              state={gameState}
              viewerSlot={myPlayer?.slot ?? 1}
              regionToggleNode={regionToggleNode}
              regionToggleCorner={regionToggleCorner}
              zoomToggleNode={zoomToggleNode}
              zoomToggleCorner={zoomToggleCorner}
              showRegionBonuses={regionDetailsMode}
              regionFocusId={focusedRegionId}
              selectedRegionId={regionDetailsMode && focusedRegionId ? focusedRegionId : undefined}
              selectedRegionColor={
                regionDetailsMode && focusedRegionId
                  ? regionColorById[focusedRegionId]
                  : undefined
              }
              hideOnMapZoomButtons={!!mapControlsBar}
              reservedBottom={mapControlsBar ? 44 : undefined}
              zoomEnabled={zoomEnabled}
              panOffset={panOffset}
              onPanChange={onPanChange}
              zoomScale={zoomScale}
              onZoomScaleChange={onZoomScaleChange}
              viewerJoinedAt={viewerJoinedAt}
            />
          </View>
          <MapControlsBarSlot state={gameState}>{mapControlsBar}</MapControlsBarSlot>
        </View>
      ) : (
        <View style={{ marginTop: -portraitReserves.topOverlap }}>
        <MapView
          state={gameState}
          viewerSlot={myPlayer?.slot ?? 1}
          regionToggleNode={regionToggleNode}
          regionToggleCorner={regionToggleCorner}
          zoomToggleNode={zoomToggleNode}
          zoomToggleCorner={zoomToggleCorner}
          showRegionBonuses={regionDetailsMode}
          regionFocusId={focusedRegionId}
          selectedRegionId={regionDetailsMode && focusedRegionId ? focusedRegionId : undefined}
          selectedRegionColor={
            regionDetailsMode && focusedRegionId
              ? regionColorById[focusedRegionId]
              : undefined
          }
          hideOnMapZoomButtons={!!mapControlsBar}
          reservedTop={portraitReserves.reservedTop}
          reservedBottom={portraitReserves.reservedBottom}
          trimMargins
          zoomEnabled={zoomEnabled}
          panOffset={panOffset}
          onPanChange={onPanChange}
          zoomScale={zoomScale}
          onZoomScaleChange={onZoomScaleChange}
          viewerJoinedAt={viewerJoinedAt}
        />
        </View>
      )}
      {!isLandscape && mapControlsBar}
      {!isLandscape && chatBar}
      {!isLandscape && hasActivity && renderChatOnlyLog && renderChatOnlyLog({ orientation: 'bottom-panel' })}
      {!isLandscape && hasActivity && !renderChatOnlyLog && (
        <RecentActivitySidebar
          gameId={game.id}
          state={gameState}
          players={game.players}
          orientation="bottom-panel"
          onViewAtEvent={onViewAtEvent}
          hideHeader
          selectedEventIndex={historicalEventIndex}
          eventIndexOffset={liveEventOffset}
          infiniteScroll
          filters={logFilters}
        />
      )}
    </View>
    </View>
      {historicalGameState && hasActivity && (
        <View className="flex-1" style={inHistoricalView ? undefined : { display: 'none' }}>
          <HistoricalActiveView
            id={game.id}
            historicalGameState={historicalGameState}
            historicalEventIndex={historicalEventIndex as number}
            totalEvents={gameState.eventCount ?? 0}
            game={game}
            myPlayer={myPlayer}
            isLandscape={isLandscape}
            isDesktop={isDesktop}
            onScrubEvent={onScrubEvent}
            onReturnToLive={onReturnToLive}
            arrowKeysEnabled={snapshotArrowKeysEnabled}
            regionToggleNode={regionToggleNode}
            regionToggleCorner={regionToggleCorner}
            zoomToggleNode={zoomToggleNode}
            zoomToggleCorner={zoomToggleCorner}
            mapControlsBar={mapControlsBar}
            zoomEnabled={zoomEnabled}
            panOffset={panOffset}
            onPanChange={onPanChange}
            zoomScale={zoomScale}
            onZoomScaleChange={onZoomScaleChange}
            showRegionBonuses={regionDetailsMode}
          />
        </View>
      )}
    </View>
  )
}
